import React, {
    ComponentProps,
    FC,
    MouseEvent,
    useContext,
    useEffect,
    useState
} from 'react';
import dynamic from 'next/dynamic';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import {
    PaginationOnPageChangeEvent,
    Breadcrumbs,
    useModal
} from '@blueprint/ui';
import { ApolloError } from 'apollo-boost';
import { ClientSidePageViewEvent } from '@blueprint/tracking/dist/Tracking';
import triggerTracking from '@blueprint/tracking';
import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk';

import {
    filtersValuesSelector,
    identifierSelector
} from '../../reducers/filters';
import { setLoggedIn } from '../../reducers/user';
import {
    resetResultsAsync,
    totalPagesSelector,
    selectPageNumber
} from '../../reducers/searchResults';
import { selectCurrentSortOrder } from '../../reducers/listingsSortOrder';
import Head from 'shared/components/Head/Head';
import AuthModal from 'shared/components/AuthModal/AuthModal';
import { DELETE_LISTING, SAVE_LISTING } from '../../queries/search/listings';
import { USER_FAVOURITES_QUERY } from '../../queries/search/listings';
import { Listings } from '../Listings';
import { getTranslator } from '../../shared/i18n/getTranslator';
import { SearchResultsHeader } from './SearchResultsHeader';
import { DiscoverMore } from '../banners/DiscoverMore';
import {
    PaginationContainer,
    SearchResultsLayoutGroup,
    StyledPagination
} from '../Layout/Layout.styles';
import { SearchContext, SeoBlurb, ServerData, Links } from '../SearchContext';
import { favouriteTracking } from '../Listings/Listings.tracking';
import { Listing } from '../Listings/Listings.types';
import {
    updateResults,
    buildQueryParamsString,
    segmentSearchEvent,
    buildCanonicalPath
} from '../helpers';
import { trackFeaturedPropertyImpressions } from '../FeaturedPropertyList/featuredPropertyTracking';
import { PropertyOrPromoItem } from '../helpers/featuredProperties';
import useIsWidthLessThanBreakpoint from 'shared/hooks/app/useIsWidthLessThanBreakpoint';
import { useIsLoggedIn } from 'shared/hooks/useIsLoggedIn';
import { removeEverythingAfterHash } from '../../shared/helpers';
import { trackPage, getAnalyticsEventData } from '../../shared/helpers';
import { AdvertSlotDfp } from 'shared/components/Adverts/AdvertSlotDfp';
import FeaturedPropertyList from '../FeaturedPropertyList';
import { identifyUser } from 'shared/helpers/segment/identifyUser';
import { getJSONLD } from '../Seo/schemaMarkup';
import { shouldIndexPath } from '../Seo/noFollow';
import { BreadcrumbsShowMobileWrapper } from './SearchResults.styles';
import searchTranslations from 'public/locales/en/search.json';
import { EmailSent } from 'search/EmailAgents/EmailSent';
import { SortOrderDropdown } from './SearchResultsHeader/SortOrderDropdown';
import { breakpoints } from '../common/constants';
import { AD_UNIT_PAGES, AD_UNIT_ROOTS } from 'shared/components/Adverts/types';

const EmailAgent = dynamic<
    ComponentProps<typeof import('../EmailAgents').EmailAgent>
>(() => import('../EmailAgents').then((module) => module.EmailAgent), {
    ssr: false
});

const EmailAgentInPageForm = dynamic<
    ComponentProps<
        typeof import('../EmailAgents/EmailAgentInPageForm').EmailAgentInPageForm
    >
>(
    () =>
        import('../EmailAgents/EmailAgentInPageForm').then(
            (module) => module.EmailAgentInPageForm
        ),
    {
        ssr: false
    }
);

export type SearchResultProps = {
    regularListings: Listing[];
    extendedListings: Listing[];
    featuredProperties: PropertyOrPromoItem[];
    canonicalPath: string;
    searchResults: ServerData['searchResults'];
    discoverMore: ServerData['discoverMore'];
    breadcrumbs: ServerData['breadcrumbs'];
    loading: boolean;
    transactionType: SeoBlurb['transactionType'];
    error?: ApolloError;
    links: Links;
};

const PAGE_NUMBER_REGEX = /pn=[0-9]+/;

const BottomPageAdWrapper = styled.div(
    ({ theme }) => css`
        padding-top: ${theme.spacing['16px']};
    `
);

const t = getTranslator(searchTranslations);

const loadingText = t('ui.loading');
const previousLabel = `< ${t('pagination.back')}`;
const nextLabel = `${t('pagination.next')} >`;

const SearchResults: FC<SearchResultProps & WithOptimizelyProps> = ({
    regularListings,
    extendedListings,
    featuredProperties,
    canonicalPath: canonicalPathFromServer,
    searchResults,
    breadcrumbs,
    discoverMore,
    loading,
    transactionType,
    links,
    error,
    optimizely
}) => {
    const emailAllInPageExperimentVariant = optimizely?.activate(
        'sr_562_email_all_in-page'
    );

    const reduxDispatch = useDispatch();
    const router = useRouter();
    const { query, asPath } = router;
    const path = removeEverythingAfterHash(asPath);

    const { state, dispatch } = useContext(SearchContext);

    const {
        searchType,
        currentSearchSource,
        userAlertId,
        isEditSavedSearchBannerVisible,
        previousSavedSearchUri,
        savedSearchAndAlerts,
        favourites,
        actionAfterAuth,
        seoAccordions
    } = state;

    const isLoggedIn = useIsLoggedIn();

    const filters = useSelector(filtersValuesSelector);
    const currentSortOrder = useSelector(selectCurrentSortOrder);
    const { selectedValue: identifierSelectedValue } = useSelector(
        identifierSelector
    );

    const totalPages = useSelector(totalPagesSelector);
    const pageNumber = useSelector(selectPageNumber);

    const [canonicalPath, setCanonicalPath] = useState(canonicalPathFromServer);
    const [isInitialRender, setIsInitialRender] = useState(true);
    const [isWidthLessThanBreakpoint] = useIsWidthLessThanBreakpoint();

    const [internalPromoParam, setInternalPromoParam] = useState('');

    const [saveListingMutation] = useMutation(SAVE_LISTING);
    const [deleteListingMutation] = useMutation(DELETE_LISTING);

    const [isAuthModalShowing, openAuthModal, closeAuthModal] = useModal();

    const pathForSignIn = process.env.SIGNIN_PATH || '/signin/';

    const saveToggle = async (id: string, isFavourite: boolean) => {
        const immediateAction = isFavourite
            ? 'REMOVE_FAVOURITE_LISTING'
            : 'ADD_FAVOURITE_LISTING';
        const rollbackAction = isFavourite
            ? 'ADD_FAVOURITE_LISTING'
            : 'REMOVE_FAVOURITE_LISTING';
        const propertyToCheck = isFavourite
            ? 'deleteFavouriteListing'
            : 'saveFavouriteListing';
        const apiCall = (id: string) =>
            isFavourite
                ? deleteListingMutation({ variables: { id } })
                : saveListingMutation({ variables: { id } });

        dispatch({
            type: immediateAction,
            payload: id
        });

        try {
            const { data } = await apiCall(id);
            if (!data?.[propertyToCheck]) {
                throw 'Update unsuccessful';
            }
        } catch (e) {
            dispatch({
                type: rollbackAction,
                payload: id
            });
        }
        return Promise.resolve(true);
    };

    const authenticateSaveToggle = async (listingId: string) => {
        const isFavourite = favourites.listings.includes(listingId);

        favouriteTracking(isFavourite);

        if (!isLoggedIn) {
            dispatch({
                type: 'SET_ACTION_AFTER_AUTH',
                payload: {
                    action: () => {
                        saveToggle(listingId, isFavourite);

                        reduxDispatch(setLoggedIn(true));
                    },
                    eventLabel: 'save_listing'
                }
            });
            openAuthModal(null);
            return false;
        }

        return saveToggle(listingId, isFavourite);
    };

    const resetActionAfterAuth = async () => {
        dispatch({
            type: 'SET_ACTION_AFTER_AUTH',
            payload: {
                action: () => null,
                eventLabel: 'save_listing'
            }
        });
    };

    useEffect(() => {
        const promoParam = buildQueryParamsString(query, 1);

        setInternalPromoParam(promoParam);
    }, [query.q]);

    // All tracking handled in one effect
    useEffect(() => {
        if (!searchResults?.analyticsTaxonomy?.page) {
            return;
        }

        const handleImpressions = () => {
            const analyticsEcommerce = searchResults.analyticsEcommerce;

            if (!analyticsEcommerce?.impressions?.length) {
                return;
            }

            const areFeaturedPropertiesDisplayed =
                featuredProperties.length > 0 && !isWidthLessThanBreakpoint;

            if (areFeaturedPropertiesDisplayed) {
                trackFeaturedPropertyImpressions(featuredProperties);
            }

            triggerTracking({
                event: 'GAEventEcommerce',
                ecommerce: {
                    currencyCode: analyticsEcommerce.currencyCode,
                    impressions: analyticsEcommerce.impressions
                }
            });
        };

        const fireClientSidePageTracking = async () => {
            const {
                analyticsTaxonomy: analyticsTaxonomyFromResponse
            } = searchResults;

            const analyticsTaxonomy = {
                ...analyticsTaxonomyFromResponse,
                location: analyticsTaxonomyFromResponse.areaName
            };

            const analyticsEventData = await getAnalyticsEventData(
                analyticsTaxonomy.page,
                analyticsTaxonomy
            );

            const clientSidePageViewEvent: ClientSidePageViewEvent = {
                event: 'clientSidePageView',
                ...analyticsEventData
            };

            // These functions must be in this order as pageView updates the
            // dataLayer with custom dimensions which the other events need
            triggerTracking(clientSidePageViewEvent);
            handleImpressions();
        };
        // The initial 'Page View' tracking is handled by the GTM container
        if (!isInitialRender) {
            fireClientSidePageTracking();
        } else {
            handleImpressions();
        }
    }, [searchResults?.analyticsTaxonomy?.url]);

    useEffect(() => {
        if (!searchResults?.analyticsTaxonomy?.page) {
            return;
        }
        const {
            analyticsTaxonomy: analyticsTaxonomyFromResponse
        } = searchResults;

        const analyticsTaxonomy = {
            ...analyticsTaxonomyFromResponse,
            location: analyticsTaxonomyFromResponse.areaName
        };

        trackPage(analyticsTaxonomy.page, analyticsTaxonomy);
        segmentSearchEvent(
            analyticsTaxonomy,
            currentSearchSource,
            searchResults.seoBlurb.category,
            filters
        );
    }, [searchResults]);

    useEffect(() => {
        identifyUser(window);

        // TODO: REMOVE AFTER EMAIL ALL EXPERIMENT FINISHED
        if (window?.dataLayer) {
            window.dataLayer.push({
                event: 'GAEvent',
                eventCategory: 'optimizely',
                eventAction: 'sr_562_email_all_in-page',
                eventLabel: emailAllInPageExperimentVariant
            });
        }
    }, []);

    useEffect(() => {
        const uri = searchResults?.metaInfo?.canonicalUri || '';
        const newCanonicalPath = buildCanonicalPath(uri);

        setCanonicalPath(newCanonicalPath);
    }, [searchResults?.metaInfo?.canonicalUri]);

    useEffect(() => {
        // This is used to block the first 'Page View' event of the page, as that is captured by GTM automatically
        setIsInitialRender(false);
    }, []);

    /**
       NOTE:
       This param is for internal ads in featured property list (Map and New Home link)
       Both links start a new search using search param in results page, e.g. "q=london&radius=0"
       Remove if Map & New Home were implemented in app-react and shared the same search context
    */
    const currentPage = typeof query.pn === 'string' ? parseInt(query.pn) : 1;

    const handleChangePage = async (
        onPageChangeEvent: PaginationOnPageChangeEvent
    ) => {
        onPageChangeEvent.nativeEvent.preventDefault();

        if (onPageChangeEvent.source === 'next') {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'click',
                eventLabel: `pagination_next`
            });
        } else if (onPageChangeEvent.source === 'previous') {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'click',
                eventLabel: `pagination_back`
            });
        } else {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'click',
                eventLabel: `page_${onPageChangeEvent.targetPage}`
            });
        }

        await updateResults(
            filters,
            router,
            searchType,
            currentSearchSource,
            currentSortOrder,
            userAlertId,
            false,
            false,
            onPageChangeEvent.targetPage
        );
    };

    const pageNumberToHref = (pageNumber: number): string => {
        const currentPath = router.asPath.replace('&view_type=list', '');

        if (PAGE_NUMBER_REGEX.test(currentPath)) {
            return currentPath.replace(PAGE_NUMBER_REGEX, `pn=${pageNumber}`);
        }

        return currentPath.indexOf('?') !== -1
            ? `${currentPath}&pn=${pageNumber}`
            : `${currentPath}?pn=${pageNumber}`;
    };

    const onResetFilters = async (evt: MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();

        const SEARCH_SOURCE = 'facets';
        reduxDispatch(
            resetResultsAsync(
                query,
                router,
                searchType,
                SEARCH_SOURCE,
                currentSortOrder,
                userAlertId
            )
        );
    };

    useQuery(USER_FAVOURITES_QUERY, {
        onCompleted: (response) => {
            dispatch({
                type: 'SET_LISTINGS_USER_FAVOURITES',
                payload: {
                    favourites: response.favourites
                }
            });
        }
    });

    const emailSent = (
        <EmailSent
            isUserLoggedIn={isLoggedIn}
            createAlert={links?.createAlert || ''}
        />
    );

    const sortOrderDropdown = (
        <SortOrderDropdown
            excludeRadiusFromResults={extendedListings.length > 0}
            userAlertId={userAlertId}
            searchType={searchType}
            dispatch={dispatch}
        />
    );

    const authModal = (
        <AuthModal
            isShowing={isAuthModalShowing}
            close={closeAuthModal}
            pathForSignIn={pathForSignIn}
            actionAfterAuth={actionAfterAuth}
            resetActionAfterAuth={resetActionAfterAuth}
        />
    );

    const isForSaleSearch = asPath.startsWith('/for-sale');

    const adUnitPage = isForSaleSearch
        ? AD_UNIT_PAGES.FOR_SALE
        : AD_UNIT_PAGES.TO_RENT;

    if (loading) {
        return (
            <h2
                data-testid="loading-search-results"
                style={{ textAlign: 'center' }}
            >
                {loadingText}
            </h2>
        );
    }

    const isInPageVariant =
        emailAllInPageExperimentVariant === 'variation_1' ||
        emailAllInPageExperimentVariant === 'variation_2';

    return (
        <>
            <Head
                pageTitle={searchResults?.metaInfo?.title || ''}
                pageDescription={searchResults?.metaInfo?.description || ''}
                canonicalPath={canonicalPath}
                index={() =>
                    shouldIndexPath(
                        asPath,
                        query,
                        searchResults?.seoBlurb?.category || '',
                        pageNumber,
                        regularListings.length,
                        extendedListings.length
                    )
                }
                schema={() =>
                    getJSONLD(
                        asPath,
                        searchResults?.metaInfo?.canonicalUri || '',
                        breadcrumbs || []
                    )
                }
            />

            <SearchResultsHeader
                breadcrumbs={breadcrumbs || []}
                previousSavedSearchUri={previousSavedSearchUri}
                savedSearchAndAlertsUri={savedSearchAndAlerts?.uri}
                userAlertId={userAlertId}
                isEditSavedSearchBannerVisible={isEditSavedSearchBannerVisible}
                pageTitle={searchResults?.title || ''}
                mapIconLink={links?.mapView || ''}
                isLoggedIn={isLoggedIn}
                sortOrderDropdown={sortOrderDropdown}
            />

            <SearchResultsLayoutGroup>
                <FeaturedPropertyList
                    properties={featuredProperties}
                    sectionType={transactionType}
                    propertyType={seoAccordions[0]?.propertyType}
                    identifier={identifierSelectedValue as string}
                    searchParam={internalPromoParam}
                />

                <Listings
                    searchType={searchType}
                    analyticsTaxonomyIdentifier={
                        searchResults?.analyticsTaxonomy?.searchIdentifier || ''
                    }
                    analyticsTaxonomy={searchResults?.analyticsTaxonomy}
                    analyticsEcommerce={searchResults?.analyticsEcommerce}
                    searchCategory={searchResults?.seoBlurb?.category ?? ''}
                    filters={filters}
                    currentSearchSource={currentSearchSource}
                    error={error}
                    regularListings={regularListings}
                    extendedListings={extendedListings}
                    onResetFilters={onResetFilters}
                    favouritesListings={favourites.listings}
                    authenticateSaveToggle={authenticateSaveToggle}
                    authModal={authModal}
                    pageNumber={pageNumber}
                />

                {!isInPageVariant ? (
                    <EmailAgent
                        path={path}
                        emailSent={emailSent}
                        asPath={asPath}
                    />
                ) : null}

                {regularListings.length > 0 && (
                    <PaginationContainer data-testid="pagination">
                        <StyledPagination
                            previousLabel={previousLabel}
                            nextLabel={nextLabel}
                            totalPages={totalPages}
                            currentPage={currentPage}
                            pageNumberToHref={pageNumberToHref}
                            onPageChange={handleChangePage}
                        />
                    </PaginationContainer>
                )}

                {isInPageVariant ? (
                    <EmailAgentInPageForm
                        path={path}
                        asPath={asPath}
                        variation={emailAllInPageExperimentVariant}
                        searchType={searchType}
                        location={filters?.location?.value}
                        isUserLoggedIn={isLoggedIn}
                        createAlert={links?.createAlert || ''}
                    />
                ) : null}

                <BreadcrumbsShowMobileWrapper>
                    <Breadcrumbs
                        data-testid="mobile-breadcrumbs"
                        data={breadcrumbs || []}
                    />
                </BreadcrumbsShowMobileWrapper>

                <DiscoverMore
                    findAgentsUri={discoverMore?.findAgentsUri || ''}
                    housePricesUri={discoverMore?.housePricesUri || ''}
                    searchArea={
                        searchResults?.analyticsTaxonomy?.areaName || ''
                    }
                />

                <BottomPageAdWrapper>
                    <AdvertSlotDfp
                        showOnly={['>', breakpoints.lsrpSidebar]}
                        position={5}
                        sizes={[
                            [320, 50],
                            [728, 90],
                            [468, 60],
                            [970, 90]
                        ]}
                        adUnitCode={[AD_UNIT_ROOTS.FOOTER, adUnitPage]}
                    />
                </BottomPageAdWrapper>
            </SearchResultsLayoutGroup>
        </>
    );
};

export default withOptimizely(SearchResults);
